import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAuditLogsByEntity } from "../../../../Services/AuditLog";
import {
  projectRegistrationPaymentHistory,
  projectRegistrationRegistrantHistory,
} from "../../../../utils/registrationAuditHistory";
import {
  formatCurrencyAmount,
  formatPaymentMethodLabel,
} from "../formatters";
import type { AppDispatch } from "../../../../redux/store";
import {
  setAuditLogs,
  setAuditHistoryExpanded,
  setIsLoadingAuditLogs,
  setPaymentHistoryRows,
  setRegistrantHistoryRows,
} from "../../../../redux/states/editParticipant.state";
import {
  selectEditParticipantAudit,
  selectEventData,
  selectEventId,
  selectIsEditMode,
  selectRegistrationData,
  selectRegistrationId,
} from "../editParticipant.selectors";

const MONTHS_BEFORE_EVENT = 18;
const MONTHS_BEFORE_MODIFICATION = 6;
const EXTENDED_MONTHS_BEFORE_MODIFICATION = 36;

function toEpochSeconds (value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
}

function secondsMonthsBefore (epochSeconds: number, months: number): number {
  const date = new Date(epochSeconds * 1000);
  date.setMonth(date.getMonth() - months);
  return Math.floor(date.getTime() / 1000);
}

function isAtLeastMonthsAfter (epochSeconds: number, months: number): boolean {
  const threshold = new Date(epochSeconds * 1000);
  threshold.setMonth(threshold.getMonth() + months);
  return Date.now() >= threshold.getTime();
}

function monthKeyFromEpoch (epochSeconds: number): string {
  const date = new Date(epochSeconds * 1000);
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${date.getUTCFullYear()}${month}`;
}

/** Default window starts 18 months before the event and runs through the current month. Without an event date it is the 6 months ending at the last modification. Expanded starts 3 years before that modification. */
export function registrationAuditRange (params: {
  eventDate?: unknown;
  lastModified?: unknown;
  expanded: boolean;
}): { fromEpochSec: number; toMonth?: string } {
  const now = Math.floor(Date.now() / 1000);
  const modified = toEpochSeconds(params.lastModified) ?? now;
  if (params.expanded) {
    return {
      fromEpochSec: secondsMonthsBefore(modified, EXTENDED_MONTHS_BEFORE_MODIFICATION),
    };
  }
  const eventDate = toEpochSeconds(params.eventDate);
  if (eventDate != null) {
    return {
      fromEpochSec: secondsMonthsBefore(eventDate, MONTHS_BEFORE_EVENT),
    };
  }
  return {
    fromEpochSec: secondsMonthsBefore(modified, MONTHS_BEFORE_MODIFICATION),
    toMonth: monthKeyFromEpoch(modified),
  };
}

/** Run once in EditParticipant index — loads audit logs and projects rows. */
export function useEditParticipantAuditLoader (): void {
  const dispatch = useDispatch<AppDispatch>();
  const registrationId = useSelector(selectRegistrationId);
  const eventId = useSelector(selectEventId);
  const registrationData = useSelector(selectRegistrationData);
  const eventData = useSelector(selectEventData);
  const audit = useSelector(selectEditParticipantAudit);
  const auditHistoryExpanded = audit.auditHistoryExpanded;
  const eventDate = eventData?.startDate ?? registrationData?.eventDate;
  const lastModified = registrationData?._ts ?? registrationData?.registrationDate;
  const auditRange = registrationAuditRange({
    eventDate,
    lastModified,
    expanded: auditHistoryExpanded,
  });

  useEffect(() => {
    if (!registrationId) {
      dispatch(setAuditLogs([]));
      return;
    }
    if (eventId && !eventData) {
      return;
    }
    let cancelled = false;
    (async () => {
      dispatch(setIsLoadingAuditLogs(true));
      try {
        const logs = await getAuditLogsByEntity({
          entityId: registrationId,
          entity: "Registration",
          fromEpochSec: auditRange.fromEpochSec,
          toMonth: auditRange.toMonth,
        });
        if (!cancelled) {
          dispatch(setAuditLogs(logs));
        }
      } catch (error) {
        console.error("Error fetching registration audit logs:", error);
        if (!cancelled) {
          dispatch(setAuditLogs([]));
        }
      } finally {
        if (!cancelled) {
          dispatch(setIsLoadingAuditLogs(false));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auditRange.fromEpochSec, auditRange.toMonth, dispatch, eventData, eventId, registrationId]);

  useEffect(() => {
    const actorContext = {
      registrantDisplayName: [
        registrationData?.personName,
        registrationData?.personLastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim(),
    };
    const programTitles = Object.fromEntries(
      (eventData?.program ?? [])
        .filter((p): p is typeof p & { id: string } => Boolean(p.id))
        .map((p) => [
          p.id,
          p.registrationPageSessionTitle || p.title || p.id,
        ]),
    );
    const rows = projectRegistrationRegistrantHistory(
      audit.auditLogs,
      actorContext,
      {
        programTitles,
        fallbackAttendeeType: registrationData?.attendeeType,
      },
    )
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
      .map((row, index) => ({
        id: row.id,
        index: index + 1,
        date: row.date,
        type: row.type,
        info: row.info,
        modified: row.modified,
        modifiedBy: row.modifiedBy,
      }));
    dispatch(setRegistrantHistoryRows(rows));
  }, [
    audit.auditLogs,
    dispatch,
    eventData?.program,
    registrationData?.attendeeType,
    registrationData?.personName,
    registrationData?.personLastName,
  ]);

  useEffect(() => {
    const actorContext = {
      registrantDisplayName: [
        registrationData?.personName,
        registrationData?.personLastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim(),
    };
    const rows = projectRegistrationPaymentHistory(
      audit.auditLogs,
      actorContext,
      { fallbackPaymentStatus: registrationData?.paymentStatus },
    ).map((row, index) => ({
      id: row.id,
      index: index + 1,
      logDate: row.date,
      paymentDate: row.date,
      status: row.paymentStatus,
      amount: formatCurrencyAmount(row.amount),
      paymentMethod: formatPaymentMethodLabel(row.paymentMethod),
      savedBy: row.savedBy,
      note: row.note || "—",
    }));
    dispatch(setPaymentHistoryRows(rows));
  }, [
    audit.auditLogs,
    dispatch,
    registrationData?.paymentStatus,
    registrationData?.personName,
    registrationData?.personLastName,
  ]);
}

export function useEditParticipantAudit () {
  const dispatch = useDispatch<AppDispatch>();
  const audit = useSelector(selectEditParticipantAudit);
  const isEditMode = useSelector(selectIsEditMode);
  const eventCreatedAt = toEpochSeconds(useSelector(selectEventData)?.createdDate);
  const showLoadMoreAuditHistory =
    isEditMode &&
    !audit.auditHistoryExpanded &&
    eventCreatedAt != null &&
    isAtLeastMonthsAfter(eventCreatedAt, MONTHS_BEFORE_EVENT);

  return {
    showLoadMoreAuditHistory,
    auditHistoryExpanded: audit.auditHistoryExpanded,
    setAuditHistoryExpanded: (expanded: boolean) =>
      dispatch(setAuditHistoryExpanded(expanded)),
    isLoadingAuditLogs: audit.isLoadingAuditLogs,
    registrantHistoryRows: audit.registrantHistoryRows,
    paymentHistoryRows: audit.paymentHistoryRows,
  };
}

export function useRefreshAuditLogs () {
  const dispatch = useDispatch<AppDispatch>();
  const registrationId = useSelector(selectRegistrationId);
  const eventId = useSelector(selectEventId);
  const registrationData = useSelector(selectRegistrationData);
  const eventData = useSelector(selectEventData);
  const auditHistoryExpanded = useSelector(selectEditParticipantAudit).auditHistoryExpanded;
  const auditRange = registrationAuditRange({
    eventDate: eventData?.startDate ?? registrationData?.eventDate,
    lastModified: registrationData?._ts ?? registrationData?.registrationDate,
    expanded: auditHistoryExpanded,
  });

  return useCallback(async () => {
    if (!registrationId) {
      return;
    }
    if (eventId && !eventData) {
      return;
    }
    dispatch(setIsLoadingAuditLogs(true));
    try {
      // The save response returns before the audit row is written.
      await new Promise((resolve) => setTimeout(resolve, 500));
      const logs = await getAuditLogsByEntity({
        entityId: registrationId,
        entity: "Registration",
        fromEpochSec: auditRange.fromEpochSec,
        toMonth: auditRange.toMonth,
      });
      dispatch(setAuditLogs(logs));
    } catch (error) {
      console.error("Error refreshing registration audit logs:", error);
    } finally {
      dispatch(setIsLoadingAuditLogs(false));
    }
  }, [auditRange.fromEpochSec, auditRange.toMonth, dispatch, eventData, eventId, registrationId]);
}
